import React, { useCallback, useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditCustomerContext, IEditCustomerContext } from '../editCustomerState/editCustomerContext';
import { ServerErrorTypes } from 'types';
import { useGoBack } from 'hooks/useGoBack';
import axios from 'axios';
import { convertUndefinedFields } from 'utils/helpers';
import { updateCustomer } from 'backend/customers';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirmNew } from 'components/ModalWindows/ModalToConfirmNew';
import { ModalSentSuccessfullyNew } from 'components/ModalWindows/ModalSentSuccessfullyNew';
import { ModalSentErrorNew } from 'components/ModalWindows/ModalSentErrorNew';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { changeTypeOfModalWindow, refetchData, dataForSubmit, typeOfModalWindow } =
		useContext<IEditCustomerContext>(EditCustomerContext);
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/clients/customers-new-ui');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateCustomer(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.CUSTOMER_NAME_ALREADY_EXIST
				) {
					changeTypeOfModalWindow('customerNameAlreadyExist');
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow}>
			{typeOfModalWindow === 'toConfirmFieldsClearing' && (
				<ModalToConfirmNew
					onConsentEvent={handleRedirect}
					onFailureEvent={handleCloseModal}
					description="cancelSaveInfo"
					typeOfInfoIcon="toSave"
				/>
			)}
			{typeOfModalWindow === 'toConfirmSubmit' && (
				<ModalToConfirmNew
					onConsentEvent={handleConfirm}
					onFailureEvent={handleCloseModal}
					description="saveInfo"
					typeOfInfoIcon="toSave"
				/>
			)}
			{typeOfModalWindow === 'success' && (
				<ModalSentSuccessfullyNew onConsentEvent={handleUpdateData} description="savedSuccessfully" />
			)}
			{typeOfModalWindow === 'error' && (
				<ModalSentErrorNew onConsentEvent={handleCloseModal} description="errorWhileSaving" />
			)}
			{typeOfModalWindow === 'customerNameAlreadyExist' && (
				<ModalSentErrorNew onConsentEvent={handleCloseModal} description="errorCustomerNameAlreadyExist" />
			)}
			{typeOfModalWindow === 'imageFileSizeExceeded' && (
				<ModalSentErrorNew
					onConsentEvent={handleCloseModal}
					description="fileSizeExceeded"
					translationOptions={{ size: '1MB' }}
				/>
			)}
			{typeOfModalWindow === 'htmlFileSizeExceeded' && (
				<ModalSentErrorNew
					onConsentEvent={handleCloseModal}
					description="fileSizeExceeded"
					translationOptions={{ size: '10MB' }}
				/>
			)}
			{typeOfModalWindow === 'fileUploadError' && (
				<ModalSentErrorNew onConsentEvent={handleCloseModal} description="fileUploadError" />
			)}
			{typeOfModalWindow === 'fileDownloadError' && (
				<ModalSentErrorNew onConsentEvent={handleCloseModal} description="fileDownloadError" />
			)}
			{typeOfModalWindow === 'invalidFileFormat' && (
				<ModalSentErrorNew
					onConsentEvent={handleCloseModal}
					description="invalidFileFormatPhoto"
					translationOptions={{ formats: '.jpeg, .jpg, .png' }}
				/>
			)}
			{typeOfModalWindow === 'invalidHtmlFileFormat' && (
				<ModalSentErrorNew
					onConsentEvent={handleCloseModal}
					description="invalidFileFormat"
					translationOptions={{ format: '.html' }}
				/>
			)}
			{typeOfModalWindow === 'tooManyFiles' && (
				<ModalSentErrorNew onConsentEvent={handleCloseModal} description="tooManyFiles" />
			)}
		</ModalWrapper>
	);
};
